// crewbit-admin/src/components/money/RecentActivityCard.tsx
//
// SPEC §Overview.7.4 + §Money.NavigationLayerRealization D5/D6.
// Admin parity component for the mobile RecentActivityCard
// (`crewbit-mobile/components/money/RecentActivityCard.tsx`, Session 118).
// Body envelope mirrors mobile (items: FinancialSummaryActivityItem[]); only
// runtime/idiom diverges — HTML <div>/<button> instead of RN View/Pressable.
//
// Up to 5 most-recent rows from `summary.recent_activity`. Each row:
//
//   [pill?]  description           [+$X.XX | -$X.XX]?  [time]
//
// Pill comes from `activityTypeToPill` (shared admin/mobile pure mapping).
// Unknown activity_type returns `label === ''` → row renders description-only
// with no pill.
//
// Row click-through per D6 mapping (paid invoice / dispute / expense rows are
// clickable; payout / kyc / receipt have no destination v1 and render
// non-interactive). Clickable rows get role="button" + tabIndex + Enter/Space
// handler (mirror TaxPrepCard pattern).
//
// Calm-empty per §3.B.1: returns null when items.length === 0. Empty-tenant
// operator sees no surface (absence is the signal).

import { useNavigate } from 'react-router-dom';
import type { FinancialSummaryActivityItem } from '../../hooks/useFinancialSummary';
import {
  activityTypeToPill,
  type ActivityPill,
  type ActivityPillColor,
} from '../../lib/activityPills';
import { formatMoney } from '../../lib/money';
import { admin, palette, radii, shadow, spacing, transition } from '../../styles/tokens';

const MAX_ROWS = 5;

interface RecentActivityCardProps {
  items: FinancialSummaryActivityItem[];
  // C3 P4 — "See all ›" tail → full activity feed (/money/activity).
  onSeeAll?: () => void;
}

interface PillTone {
  fg: string;
  bg: string;
}

function pillTone(color: ActivityPillColor): PillTone {
  switch (color) {
    case 'success':
      return { fg: palette.green, bg: palette.greenWash };
    case 'warning':
      return { fg: palette.amber, bg: palette.amberWash };
    case 'info':
      return { fg: palette.accent, bg: palette.accentWash };
    case 'neutral':
    default:
      return { fg: palette.g600, bg: palette.g100 };
  }
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// D6 mapping. Returns null when activity_type has no destination v1.
function destinationFor(item: FinancialSummaryActivityItem): string | null {
  if (item.entity_id === null) return null;
  switch (item.activity_type) {
    case 'payment_recorded':
    case 'invoice_paid':
    case 'project_payment_recorded':
    case 'invoice_emailed':
    case 'invoice_created':
    case 'invoice_sent':
      return `/money/invoices?invoice_id=${item.entity_id}`;
    case 'expense_recorded':
    case 'vendor_bill_paid':
      return `/money/expenses?expense_id=${item.entity_id}`;
    case 'dispute_opened':
    case 'dispute_charged_back':
      return `/money/disputes?dispute_id=${item.entity_id}`;
    default:
      return null;
  }
}

interface RowProps {
  item: FinancialSummaryActivityItem;
  pill: ActivityPill;
  destination: string | null;
  isLast: boolean;
}

function Row({ item, pill, destination, isLast }: RowProps) {
  const navigate = useNavigate();
  const tone = pillTone(pill.color);
  const showAmount = item.amount !== null && item.direction !== null;
  const amountText = showAmount
    ? `${item.direction === 'in' ? '+' : '-'}${formatMoney(Math.abs(item.amount as number))}`
    : null;
  const amountColor = item.direction === 'in' ? palette.green : palette.red;
  const interactive = destination !== null;

  const handleActivate = () => {
    if (destination) void navigate(destination);
  };

  const innerContent = (
    <>
      {pill.label !== '' && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: radii.full,
            background: tone.bg,
            color: tone.fg,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.2,
            flexShrink: 0,
          }}
        >
          {pill.label}
        </span>
      )}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13.5,
          color: admin.body,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'left',
        }}
      >
        {item.description ?? ''}
      </span>
      {amountText !== null && (
        <span
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: amountColor,
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          {amountText}
        </span>
      )}
      <span
        style={{
          fontSize: 12,
          color: admin.caption,
          minWidth: 56,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {formatRelativeTime(item.created_at)}
      </span>
    </>
  );

  const sharedStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    width: '100%',
    padding: '10px 0',
    borderBottom: isLast ? 'none' : `1px solid ${palette.g100}`,
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    transition: `background ${transition.fast}`,
  };

  if (!interactive) {
    return <div style={{ ...sharedStyle, cursor: 'default' }}>{innerContent}</div>;
  }

  return (
    <button
      type="button"
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      }}
      aria-label={`${pill.label || 'Activity'}: ${item.description ?? ''}`}
      style={{ ...sharedStyle, cursor: 'pointer', textAlign: 'left' }}
    >
      {innerContent}
    </button>
  );
}

export function RecentActivityCard({ items, onSeeAll }: RecentActivityCardProps) {
  const visible = items.slice(0, MAX_ROWS);
  if (visible.length === 0) return null;

  return (
    <section
      aria-label="Recent activity"
      style={{
        background: palette.white,
        borderRadius: admin.cardRadius,
        border: `1px solid ${admin.border}`,
        padding: spacing.lg,
        boxShadow: shadow.card,
        marginBottom: spacing.lg,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: admin.header,
          marginBottom: spacing.sm,
        }}
      >
        Recent activity
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {visible.map((item, idx) => (
          <Row
            key={item.id}
            item={item}
            pill={activityTypeToPill(item.activity_type)}
            destination={destinationFor(item)}
            isLast={idx === visible.length - 1}
          />
        ))}
      </div>
      {onSeeAll && (
        <button
          type="button"
          onClick={onSeeAll}
          aria-label="See all activity"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            width: '100%',
            marginTop: spacing.sm,
            padding: '6px 0 0',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 12.5,
            fontWeight: 500,
            color: palette.accent,
          }}
        >
          See all ›
        </button>
      )}
    </section>
  );
}
